import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type {
	FieldArrayRenderProps,
	FormRenderProps,
} from "@tql/ui-components-react/kendo/form";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	chevronLeft,
	chevronRight,
} from "@tql/ui-components-react/untitled/icons";
import { useEffect, useMemo, useRef, useState } from "react";

import type { Stop } from "~/features/ftl/shared/ftl-stop.ts";

import { StopDetail } from "../stop-details.tsx";
import { getStopBadgeClassName } from "../stop-details-validator/stop-details-validator.ts";
import { StopNavigationPanel } from "../stop-navigation-panel/stop-navigation-panel.tsx";
import * as styles from "./stop-details-with-navigation.module.css";

export function StopDetailsWithNavigation({
	value,
	formRenderProps,
	lastSubmitTime,
}: Readonly<FieldArrayRenderProps> & {
	formRenderProps: FormRenderProps;
	lastSubmitTime: number;
}) {
	const stops = (value as Stop[]) || [];
	const [activeStopIndex, setActiveStopIndex] = useState(0);

	const errorKeys = Object.keys(
		(formRenderProps.errors ?? {}) as Record<string, unknown>,
	);

	const firstErrorStopIndex = useMemo(() => {
		const stopIndexRegex = /^stops\[(\d+)\]/;
		return (
			errorKeys
				.filter((key) => {
					const errorValue = (
						formRenderProps.errors as Record<string, unknown>
					)[key];
					return (
						errorValue !== "" && errorValue !== null && errorValue !== undefined
					);
				})
				.map((key) => {
					const match = stopIndexRegex.exec(key);
					return match?.[1];
				})
				.filter((v): v is string => v !== undefined)
				.map(Number)
				.sort((a, b) => a - b)[0] ?? -1
		);
	}, [errorKeys, formRenderProps.errors]);

	const firstErrorStopIndexRef = useRef(firstErrorStopIndex);
	firstErrorStopIndexRef.current = firstErrorStopIndex;

	useEffect(() => {
		if (lastSubmitTime > 0 && firstErrorStopIndexRef.current !== -1) {
			setActiveStopIndex(firstErrorStopIndexRef.current);
		}
	}, [lastSubmitTime]);

	const handlePrevious = (event: React.MouseEvent) => {
		event.preventDefault();
		if (activeStopIndex > 0) {
			setActiveStopIndex(activeStopIndex - 1);
		}
	};

	const handleNext = (event: React.MouseEvent) => {
		event.preventDefault();
		if (activeStopIndex < stops.length - 1) {
			setActiveStopIndex(activeStopIndex + 1);
		}
	};

	const isFirstStop = activeStopIndex === 0;
	const isLastStop = activeStopIndex === stops.length - 1;

	const getStopLabel = (stop: Stop, currentIndex: number): string => {
		if (!stop.stopType) return "";

		const stopTypeCount = stops
			.slice(0, currentIndex + 1)
			.filter((currentStop) => currentStop.stopType === stop.stopType).length;

		return `${stop.stopType} ${stopTypeCount}`;
	};

	return (
		<div className={styles.container}>
			<div className={styles.navigationPanel}>
				<StopNavigationPanel
					activeStopIndex={activeStopIndex}
					formRenderProps={formRenderProps}
					lastSubmitTime={lastSubmitTime}
					onStopSelect={setActiveStopIndex}
					stops={stops}
				/>
			</div>
			<div className={styles.detailsPanel}>
				{stops.map((stop, index) => {
					const badgeClassName = getStopBadgeClassName(
						formRenderProps,
						index,
						lastSubmitTime > 0,
					);
					const stopBadgeLabel = getStopLabel(stop, index);

					return (
						<div
							key={stop.id}
							className={"k-col-span-1"}
							style={{ display: index === activeStopIndex ? "block" : "none" }}
						>
							<StopDetail
								badgeClassName={badgeClassName}
								index={index}
								stop={stop}
								stopBadgeLabel={stopBadgeLabel}
							/>
						</div>
					);
				})}

				<div className={styles.navigationButtons}>
					<Button
						aria-disabled={isFirstStop}
						aria-label={"Navigate to previous stop"}
						disabled={isFirstStop}
						onClick={handlePrevious}
						startIcon={<SvgIcon icon={chevronLeft} />}
						variant={"secondary"}
					>
						Previous Stop
					</Button>
					<Button
						aria-disabled={isLastStop}
						aria-label={"Navigate to next stop"}
						disabled={isLastStop}
						endIcon={<SvgIcon icon={chevronRight} />}
						onClick={handleNext}
						variant={"secondary"}
					>
						Next Stop
					</Button>
				</div>
			</div>
		</div>
	);
}
